import { IconCheckFill } from "@/assets/icons/geist/IconCheckFill";
import { StepContent } from "@/components/environments/onboarding/StepContent";
import { Button } from "@/components/flexkit/Button";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Heading3 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { useGetEditorForEnv } from "@/hooks/use-editor-for-env";
import { useOpenEditor } from "@/hooks/use-open-editor";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { GitCommit, GitFileChange } from "@/routes/environments/details/GitStatusPopOver";
import { type FC } from "react";

export const CommitFilesStep: FC<{ environment?: PlainEnvironment; completed: boolean; onNext: () => void }> = ({
    environment,
    completed,
    onNext,
}) => {
    const openEditor = useOpenEditor();
    const { selectedEditor } = useGetEditorForEnv(environment?.id);
    return (
        <StepContent>
            <div className="flex flex-col items-center gap-4 @md:flex-row">
                <div className="flex shrink flex-col">
                    {completed ? <ContentCompleted /> : <Content environment={environment} />}
                </div>
            </div>
            <div className="flex gap-4">
                {!completed && (
                    <SkeletonBlock ready={!!selectedEditor} className="h-[30px] w-[150px]">
                        <Button
                            variant="secondary"
                            onClick={() => selectedEditor && openEditor(selectedEditor, environment)}
                            data-tracking-id="open-selected-editor-onboarding-commit-files-step"
                        >
                            Open {selectedEditor?.name}
                        </Button>
                    </SkeletonBlock>
                )}
                {completed && (
                    <Button
                        variant="primary"
                        onClick={onNext}
                        data-tracking-id="next-step-onboarding-commit-files-step"
                    >
                        Next
                    </Button>
                )}
            </div>
        </StepContent>
    );
};

const Content: FC<{ environment?: PlainEnvironment }> = ({ environment }) => {
    const hasChangedFiles = (environment?.status?.content?.git?.changedFiles?.length ?? 0) > 0;
    const hasUnpushedCommits = (environment?.status?.content?.git?.unpushedCommits?.length ?? 0) > 0;
    return (
        <div className="flex max-w-2xl flex-col gap-2">
            <Heading3 className="text-base font-bold">Commit and push the devcontainer and automation files</Heading3>
            <Text className="text-sm text-content-secondary">
                Add your dev container and automations files to your repository so that others can re-use the
                configuration. You can commit these to any branch, it doesn&apos;t have to be main.
            </Text>
            {hasChangedFiles && (
                <div className="mt-2 flex flex-col">
                    <Text className="text-sm font-bold">Uncomitted files</Text>
                    {environment?.status?.content?.git?.changedFiles.map((file) => (
                        <GitFileChange key={file.path} file={file} />
                    ))}
                </div>
            )}
            {hasUnpushedCommits && (
                <div className="mt-2 flex flex-col">
                    <Text className="text-sm font-bold">Unpushed commits</Text>
                    {environment?.status?.content?.git?.unpushedCommits.map((commit) => (
                        <GitCommit key={commit} commit={commit} />
                    ))}
                </div>
            )}
        </div>
    );
};

const ContentCompleted: FC = () => {
    return (
        <div className="flex flex-col gap-2">
            <Heading3 className="flex items-center gap-1 text-base font-bold">
                <IconCheckFill className="text-content-green" size="lg" />
                Files committed and pushed
            </Heading3>
        </div>
    );
};
